import { DetailedHTMLProps, FC, HTMLAttributes, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

import { useAnimationControl } from '~/features/accessibility/hooks/useAnimationControl';
import { useUSPItemContent } from '~/features/navigation/components/N21MetaHeader/components/MetaHeaderItems/useUSPItemContent';
import { MetaHeaderSingleItemUnion } from '~/features/navigation/components/N21MetaHeader/types';
import { PauseButton } from '~/shared/components/PauseButton/PauseButton';

import styles from './usp-rotator.module.css';

type USPRotatorProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
	items: MetaHeaderSingleItemUnion[];
	intervalMs?: number;
};

type USPItemProps = {
	item: MetaHeaderSingleItemUnion;
	active?: boolean;
	noAnimation?: boolean;
};

const USPItem: FC<USPItemProps> = ({ item, active, noAnimation }) => {
	const { iconElement, parsedRichText } = useUSPItemContent(item);

	return (
		<div className={clsx(styles.item, { [styles.active]: active, [styles.noAnimation]: noAnimation })}>
			{iconElement}
			<div className={styles.itemText}>{parsedRichText}</div>
		</div>
	);
};

export const USPRotator: FC<USPRotatorProps> = ({ items, intervalMs = 3000, className, ...rest }) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const { shouldDisableAnimations, isPaused, togglePause, prefersReducedMotion } = useAnimationControl();

	const rotationTimeoutRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		if (rotationTimeoutRef.current) {
			clearTimeout(rotationTimeoutRef.current);
		}
		if (items.length < 2 || shouldDisableAnimations || isPaused) {
			return;
		}
		rotationTimeoutRef.current = window.setTimeout(() => {
			setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
		}, intervalMs);
		return () => {
			if (rotationTimeoutRef.current) {
				clearTimeout(rotationTimeoutRef.current);
			}
		};
	}, [items.length, shouldDisableAnimations, isPaused, intervalMs, currentIndex]);

	if (!items.length) {
		return null;
	}

	return (
		<div
			className={clsx(styles.root, className)}
			{...rest}
		>
			{items.length > 1 && (
				<PauseButton
					isPaused={isPaused}
					onToggle={togglePause}
					ariaLabel={isPaused ? 'Resume USP animations' : 'Pause USP animations'}
					variant="plain"
					hideVisually={false}
					style={{
						position: 'absolute',
						top: '5px',
						right: '15px',
						transition: 'opacity 0.2s ease',
						opacity: 0,
						zIndex: 1,
					}}
					onFocus={(e) => {
						e.currentTarget.style.opacity = '1';
					}}
					onBlur={(e) => {
						e.currentTarget.style.opacity = '0';
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.opacity = '1';
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.opacity = '0';
					}}
				/>
			)}
			{items.map((singleItem, idx) => (
				<USPItem
					item={singleItem}
					active={idx === currentIndex}
					key={idx}
					noAnimation={prefersReducedMotion}
				/>
			))}
		</div>
	);
};
